import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  max-width: 10000px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(90deg, rgb(170, 39, 13), rgb(231, 145, 91));
  min-height: 100vh;
  color: #fff;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const Heading = styled.h1`
  margin: 0 0 1rem;
  font-size: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const SubText = styled.p`
  margin: 0 0 1.25rem;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  padding: 1.5rem;
  color: #1f2937;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  background: #0ea5e9;
  border: none;
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #0284c7;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0.65rem 1rem;
  }
`;

const DangerButton = styled(Button)`
  background: #dc2626;

  &:hover {
    background: #b91c1c;
  }
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const PackageCard = styled.div`
  border: 2px solid ${(props) => (props.$isEditing ? '#0ea5e9' : '#e5e7eb')};
  border-radius: 12px;
  padding: 1rem;
  background: #fff;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 180px;
  background: #f3f4f6;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b4f38;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;

  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  resize: vertical;
  min-height: 80px;
  font-family: 'Inter', sans-serif;

  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const CardTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: #1f2937;
`;

const CardMeta = styled.p`
  margin: 0.35rem 0;
  font-size: 0.85rem;
  color: #6b7280;
`;

const CardButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;

  button {
    flex: 1;
    min-width: 80px;
  }

  @media (max-width: 480px) {
    button {
      flex: 1 1 48%;
    }
  }
`;

const SmallButton = styled(Button)`
  padding: 0.5rem 1rem;
  font-size: 0.85rem;

  @media (max-width: 480px) {
    padding: 0.45rem 0.8rem;
  }
`;

const SmallDangerButton = styled(DangerButton)`
  padding: 0.5rem 1rem;
  font-size: 0.85rem;

  @media (max-width: 480px) {
    padding: 0.45rem 0.8rem;
  }
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #64748b;
`;

const UploadImageButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  border: 2px dashed #d1d5db;
  background: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #6b7280;
  transition: all 0.2s ease;
  margin-bottom: 0.75rem;

  &:hover {
    border-color: #0ea5e9;
    background: #eff6ff;
    color: #0ea5e9;
  }
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: ${(props) => (props.$error ? '#fee2e2' : '#dcfce7')};
  color: ${(props) => (props.$error ? '#991b1b' : '#166534')};
  border: 1px solid ${(props) => (props.$error ? '#fca5a5' : '#86efac')};
`;

const AdminHennaManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  // Load packages from localStorage on component mount
  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = () => {
    try {
      const stored = localStorage.getItem('hennaPackagesAdmin');
      if (stored) {
        setPackages(JSON.parse(stored));
      } else {
        // Initialize with default packages
        const defaultPackages = [
          {
            id: 'signature',
            name: 'Flower Bloom Henna',
            price: 50,
            duration: '20 minutes',
            description: 'Flower',
            imageFile: null,
          },
          {
            id: 'valentines',
            name: 'Couple Heart Henna',
            price: 30,
            duration: '30 minutes',
            description: 'Aesthetic couple heart henna tattoo.',
            imageFile: null,
          },
          {
            id: 'baybayin',
            name: 'Baybayin text Henna',
            price: 30,
            duration: '20 minutes',
            description: 'Hand written baybayin text henna tattoo.',
            imageFile: null,
          },
          {
            id: 'mandala-glow',
            name: 'Henna Cat',
            price: 30,
            duration: '5 minutes',
            description: 'Henna cat with foot steps.',
            imageFile: null,
          },
          {
            id: 'bridal-lace',
            name: 'Henna Headphone',
            price: 30,
            duration: '20 minutes',
            description: 'Cute henna headphone design.',
            imageFile: null,
          },
          {
            id: 'butterfly-bliss',
            name: 'Butterfly Henna',
            price: 70,
            duration: '25 minutes',
            description: 'Delicate butterfly pattern for a subtle look.',
            imageFile: null,
          },
          {
            id: 'festival-bloom',
            name: 'Butterfly 2 Henna',
            price: 140,
            duration: '40 minutes',
            description: 'Intricate butterfly blend suited for events and parties.',
            imageFile: null,
          },
          {
            id: 'scripted-love',
            name: 'Butterfly 3 Henna',
            price: 90,
            duration: '30 minutes',
            description: 'Custom butterfly design with decorative details.',
            imageFile: null,
          },
          {
            id: 'sunrise-cuff',
            name: 'Cat Face Henna',
            price: 110,
            duration: '35 minutes',
            description: 'Adorable cat face design with sunrise motifs.',
            imageFile: null,
          },
          {
            id: 'couple-eternity',
            name: 'Panda Henna',
            price: 160,
            duration: '40 minutes',
            description: 'Cute panda design for couples and friends.',
            imageFile: null,
          },
        ];
        setPackages(defaultPackages);
        localStorage.setItem('hennaPackagesAdmin', JSON.stringify(defaultPackages));
      }
    } catch (err) {
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (pkg) => {
    setEditingId(pkg.id);
    setFormData({ ...pkg });
    setError('');
    setMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({
          ...prev,
          imageFile: event.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description || !formData.price || !formData.duration) {
      setError('Please fill in all fields');
      return;
    }

    const updated = packages.map((pkg) =>
      pkg.id === editingId ? formData : pkg
    );

    setPackages(updated);
    localStorage.setItem('hennaPackagesAdmin', JSON.stringify(updated));

    setEditingId(null);
    setFormData({});
    setMessage('Package updated successfully!');
    setTimeout(() => setMessage(''), 3000);
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
    setError('');
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      const updated = packages.filter((pkg) => pkg.id !== id);
      setPackages(updated);
      localStorage.setItem('hennaPackagesAdmin', JSON.stringify(updated));
      setMessage('Package deleted successfully!');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleAddNew = () => {
    const newId = `custom-${Date.now()}`;
    const newPackage = {
      id: newId,
      name: 'New Henna Package',
      price: 50,
      duration: '30 minutes',
      description: 'New custom henna package',
      imageFile: null,
    };
    setPackages([...packages, newPackage]);
    handleEditStart(newPackage);
  };

  if (loading) {
    return (
      <Wrap>
        <Heading>Henna Management</Heading>
        <Card>
          <EmptyState>Loading packages...</EmptyState>
        </Card>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Heading>Henna Packages Management</Heading>
      <SubText>Edit henna package details, descriptions, and images</SubText>

      <Card>
        <ButtonGroup>
          <Button onClick={handleAddNew}>+ Add New Package</Button>
        </ButtonGroup>

        {message && <Message>{message}</Message>}
        {error && <Message $error>{error}</Message>}

        {packages.length === 0 ? (
          <EmptyState>No packages found</EmptyState>
        ) : (
          <PackagesGrid>
            {packages.map((pkg) => {
              const isEditing = editingId === pkg.id;

              return (
                <PackageCard key={pkg.id} $isEditing={isEditing}>
                  {isEditing ? (
                    <>
                      <Label htmlFor={`name-${pkg.id}`}>Package Name</Label>
                      <Input
                        id={`name-${pkg.id}`}
                        type="text"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        placeholder="Package name"
                      />

                      <Label htmlFor={`price-${pkg.id}`}>Price (PHP)</Label>
                      <Input
                        id={`price-${pkg.id}`}
                        type="number"
                        name="price"
                        value={formData.price || 0}
                        onChange={handleInputChange}
                        placeholder="Price"
                      />

                      <Label htmlFor={`duration-${pkg.id}`}>Duration</Label>
                      <Input
                        id={`duration-${pkg.id}`}
                        type="text"
                        name="duration"
                        value={formData.duration || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., 20 minutes"
                      />

                      <Label htmlFor={`desc-${pkg.id}`}>Description</Label>
                      <Textarea
                        id={`desc-${pkg.id}`}
                        name="description"
                        value={formData.description || ''}
                        onChange={handleInputChange}
                        placeholder="Package description"
                      />

                      <Label>Package Image</Label>
                      {formData.imageFile && (
                        <ImagePreview>
                          <img src={formData.imageFile} alt="preview" />
                        </ImagePreview>
                      )}
                      <FileInput
                        id={`file-${pkg.id}`}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <UploadImageButton
                        type="button"
                        onClick={() => document.getElementById(`file-${pkg.id}`).click()}
                      >
                        📸 Choose Image
                      </UploadImageButton>

                      <CardButtonGroup>
                        <SmallButton onClick={handleSave}>Save</SmallButton>
                        <SmallButton onClick={handleCancel} style={{ background: '#6b7280' }}>
                          Cancel
                        </SmallButton>
                      </CardButtonGroup>
                    </>
                  ) : (
                    <>
                      {pkg.imageFile && (
                        <ImagePreview>
                          <img src={pkg.imageFile} alt={pkg.name} />
                        </ImagePreview>
                      )}
                      <CardTitle>{pkg.name}</CardTitle>
                      <CardMeta>Price: PHP {pkg.price}</CardMeta>
                      <CardMeta>Duration: {pkg.duration}</CardMeta>
                      <CardMeta>
                        Description: {pkg.description.substring(0, 60)}
                        {pkg.description.length > 60 ? '...' : ''}
                      </CardMeta>

                      <CardButtonGroup>
                        <SmallButton onClick={() => handleEditStart(pkg)}>Edit</SmallButton>
                        <SmallDangerButton onClick={() => handleDelete(pkg.id)}>Delete</SmallDangerButton>
                      </CardButtonGroup>
                    </>
                  )}
                </PackageCard>
              );
            })}
          </PackagesGrid>
        )}
      </Card>
    </Wrap>
  );
};

export default AdminHennaManagement;
